import {
  navApplications,
  navSuite,
  navSites,
  navMonitor,
  navDocumentation,
  navUsers,
  navSystemsManager,
  navAdmin,
  navOverview,
  navHome,
  navFeedback,
  navAgents,
} from '@harbor/elements/icons';

import {
  ADMIN_URL_BASE,
  ADMIN_URL_DATA_MANAGMENT,
  ADMIN_URL_ENABLEMENT,
  ADMIN_URL_LABS_EVENTS,
  ADMIN_URL_LABS_GENERATION,
  ADMIN_URL_SAFE,
  ADMIN_URL_ASSESSMENT,
  ADMIN_URL_CAMPAIGNS,
  ADMIN_URL_TOOLBOX_CONFIG,
  ADMIN_URL_USER_ACCESS,
} from '../components/Admin/PageStructure/admin-values';

import {
  ROLLOUT_HIDDEN_ADMIN_TITLES,
  ROLLOUT_HIDDEN_NAV_KEYS,
} from '../config/rolloutFlags';

interface NavItem {
  key: string;
  value: string;
  label: string;
  iconAsset: () => string;
  path: string;
  pageView: string;
  beta?: boolean;
  /**
   * Hide this item unless the user has a live Salesforce session, on top of `pageView`. For pages
   * whose API refuses a Cisco-SSO-only caller, so the nav does not offer a link to a 403.
   */
  requiresSalesforce?: boolean;
  subMenu?: NavItemSubMenu;
  separator?: boolean;
}

interface SubMenuItem {
  type?: 'title' | 'link';
  title: string; // only require title for non 'link' type
  route?: string;
  pageView?: string;
}

interface NavItemSubMenu {
  title: string;
  items: SubMenuItem[];
  recents: boolean;
}

const BASE_NAV_ITEMS: NavItem[] = [
  {
    key: 'portal',
    value: 'Portal',
    label: 'Portal',
    iconAsset: navHome,
    path: '/portal',
    pageView: 'page.dashboard.view',
    beta: true,
  },
  {
    key: 'dashboard',
    value: 'Dashboard',
    label: 'Dashboard',
    iconAsset: navApplications,
    path: '/dashboard',
    pageView: 'page.dashboard.view',
    beta: true,
  },
  {
    key: 'leader',
    value: 'Leader',
    label: 'Leader',
    iconAsset: navSuite,
    path: '/leader',
    pageView: 'page.leader.view',
  },
  {
    key: 'customer',
    value: 'Customers',
    label: 'Customers',
    iconAsset: navSites,
    path: '/customers',
    pageView: 'page.customers.view',
    subMenu: {
      title: 'Customer',
      items: [{ type: 'link', title: 'All Customers', route: '/customers' }],
      recents: true,
    },
  },
  {
    key: 'opportunity',
    value: 'Opportunities',
    label: 'Opportunities',
    iconAsset: navMonitor,
    path: '/opportunity',
    pageView: 'page.opportunities.view',
    subMenu: {
      title: 'Opportunity',
      items: [
        { type: 'link', title: 'All Opportunities', route: '/opportunity' },
      ],
      recents: true,
    },
  },
  {
    key: 'enablement',
    value: '/enablement',
    path: '/enablement',
    label: 'Enablement',
    iconAsset: navDocumentation,
    pageView: 'page.enablement.view',
    beta: true,
  },
  {
    key: 'workshops',
    value: 'workshops',
    label: 'Workshops',
    path: '/workshops',
    iconAsset: navOverview,
    pageView: 'page.workshops.view',
    beta: true,
    subMenu: {
      title: 'Workshops',
      recents: false,
      items: [
        {
          type: 'link',
          title: 'Upcoming Events',
          route: '/workshops/upcoming-events',
        },
        {
          type: 'link',
          title: 'Previous Events',
          route: '/workshops/previous-events',
        },
        {
          type: 'link',
          title: 'Train the Trainer',
          route: '/workshops/train-the-trainer',
        },
      ],
    },
  },
  {
    key: 'lab',
    value: 'Labs',
    label: 'Labs',
    iconAsset: navAgents,
    path: '/lab/scenarios',
    // Reuses the workshops permission, the way Solutions reuses the admin one: `page.labs.view`
    // is not seeded yet, and a nav item can only name one. Whoever runs the workshop is who runs
    // the pods. The route itself also lets enablement through.
    pageView: 'page.workshops.view',
    beta: true,
    subMenu: {
      title: 'Labs',
      recents: false,
      items: [
        // Authoring first, then the live pods. The two halves stay apart everywhere else, so
        // they stay apart here.
        { type: 'link', title: 'Scenarios', route: '/lab/scenarios' },
        { type: 'link', title: 'Runs', route: '/lab/runs' },
        { type: 'link', title: 'Operations', route: '/lab/operations' },
      ],
    },
  },
  {
    key: 'safe',
    value: 'SAFE',
    label: 'SAFE',
    iconAsset: navOverview,
    // An assessment belongs to a workshop, so there is no link that opens "SAFE Risk" — the tab goes
    // to the workshop list, and the two mode entries are that list filtered by type. Landing on a
    // list of a customer's own workshops is also the step an SE has to take first now.
    path: '/engagements',
    pageView: 'page.safe.view',
    // A SAFE assessment is customer engagement work and `api/db/safe/*` and `api/db/workshop/*`
    // enforce the same rule server-side (`HasSalesforceSession`), so a Cisco-SSO-only user does not
    // see the tab at all.
    requiresSalesforce: true,
    subMenu: {
      title: 'SAFE',
      recents: false,
      items: [
        { type: 'link', title: 'Workshops', route: '/engagements' },
        { type: 'link', title: 'SAFE Risk', route: '/engagements?type=safe-risk' },
        { type: 'link', title: 'SAFE Architect', route: '/engagements?type=safe-architect' },
      ],
    },
  },
  {
    // The reusable-assessment surface: an SE opens a published assessment (workshop pre-work, a
    // feedback form, a survey) and fills it in. Unlike SAFE this is not gated on a Salesforce
    // session — an assessment need not hang off a customer account, and may allow anonymous
    // responses — so `requiresSalesforce` is deliberately absent.
    key: 'assessment',
    value: 'Assessment',
    label: 'Assessment',
    iconAsset: navFeedback,
    path: '/assessments',
    pageView: 'page.assessment.view',
    subMenu: {
      title: 'Assessment',
      recents: false,
      items: [{ type: 'link', title: 'All Assessments', route: '/assessments' }],
    },
  },
  {
    // The modular-campaign surface: an SE opens a published campaign and works its step tree. Like
    // the assessment surface it is not gated on a Salesforce session — a campaign's enablement
    // steps need not hang off a customer account — so `requiresSalesforce` is deliberately absent.
    key: 'campaign',
    value: 'Campaign',
    label: 'Campaign',
    iconAsset: navSuite,
    path: '/campaigns',
    pageView: 'page.campaign.view',
    subMenu: {
      title: 'Campaign',
      recents: false,
      items: [{ type: 'link', title: 'All Campaigns', route: '/campaigns' }],
    },
  },
  {
    key: 'mssp',
    value: 'MSSP',
    label: 'MSSP',
    iconAsset: navUsers,
    path: '/mssp',
    pageView: 'page.mssp.view',
  },
  {
    key: 'approvals',
    value: 'Approvals',
    label: 'Approvals',
    iconAsset: navSystemsManager,
    path: '/approvals',
    pageView: 'page.approvals.view',
  },
  {
    key: 'p2p',
    value: 'P2P',
    label: 'P2P',
    iconAsset: navFeedback,
    path: '/p2p',
    pageView: 'page.p2p.view',
  },
  {
    key: 'cyber-bytes',
    value: 'Cyber Bytes',
    label: 'Cyber Bytes',
    iconAsset: navDocumentation,
    path: '/cyber-bytes',
    pageView: 'page.p2p.view',
  },
  {
    key: 'solutions',
    value: 'Solutions',
    label: 'Solutions',
    iconAsset: navOverview,
    path: '/solutions',
    pageView: 'page.admin.view',
    subMenu: {
      title: 'Solutions',
      items: [
        {
          type: 'link',
          title: 'Architectures',
          route: '/solutions/architecture',
        },
        {
          type: 'link',
          title: 'Products',
          route: '/solutions/product',
        },
      ],
      recents: true,
    },
  },
  {
    key: 'admin',
    value: 'Admin',
    label: 'Admin',
    iconAsset: navAdmin,
    path: ADMIN_URL_BASE,
    pageView: 'page.admin.view',
    separator: true,
    subMenu: {
      title: 'Admin',
      items: [
        {
          type: 'link',
          title: 'Users and Access',
          route: ADMIN_URL_USER_ACCESS,
        },
        {
          type: 'link',
          title: 'Data Management',
          route: ADMIN_URL_DATA_MANAGMENT,
        },
        {
          type: 'link',
          title: 'Toolbox Configuration',
          route: ADMIN_URL_TOOLBOX_CONFIG,
        },
        {
          type: 'link',
          title: 'Enablement',
          route: ADMIN_URL_ENABLEMENT,
        },
        // Labs, in the two halves they are actually worked on in: authoring a lab, and running an
        // event for one. Adjacent rather than nested because the submenu renderer draws every
        // entry as a link - a `type: 'title'` heading would look clickable and do nothing.
        {
          type: 'link',
          title: 'Lab Generation',
          route: ADMIN_URL_LABS_GENERATION,
        },
        {
          type: 'link',
          title: 'Lab Event Management',
          route: ADMIN_URL_LABS_EVENTS,
        },
        // Its own entry rather than a tab under Data Management: the `SAFE Admin` role has
        // `page.safeAdmin.view` and nothing else, so it needs a link it can actually reach.
        {
          type: 'link',
          title: 'SAFE',
          route: ADMIN_URL_SAFE,
          pageView: 'page.safeAdmin.view',
        },
        // Its own entry, gated on its own page permission, for the same reason as SAFE: the
        // `Assessment Admin` role holds `page.assessmentAdmin.view` and nothing else, so it needs
        // a link it can actually reach.
        {
          type: 'link',
          title: 'Assessments',
          route: ADMIN_URL_ASSESSMENT,
          pageView: 'page.assessmentAdmin.view',
        },
        // Its own entry, gated on its own page permission, for the same reason as SAFE and
        // Assessments: the `Campaign Admin` role holds `page.campaignAdmin.view` and needs a link
        // it can reach without the rest of the admin area.
        {
          type: 'link',
          title: 'Campaigns',
          route: ADMIN_URL_CAMPAIGNS,
          pageView: 'page.campaignAdmin.view',
        },
      ],
      recents: true,
    },
  },
];

// Hide surfaces that are deliberately out of the current rollout (see rolloutFlags). Drop the
// hidden top-level items, and prune the hidden entries out of any submenu (e.g. the Admin menu's
// Lab Generation / Lab Event Management / SAFE / Assessments links).
const applyRolloutHides = (items: NavItem[]): NavItem[] =>
  items
    .filter((item) => !ROLLOUT_HIDDEN_NAV_KEYS.has(item.key))
    .map((item) =>
      item.subMenu
        ? {
            ...item,
            subMenu: {
              ...item.subMenu,
              items: item.subMenu.items.filter(
                (sub) => !ROLLOUT_HIDDEN_ADMIN_TITLES.has(sub.title),
              ),
            },
          }
        : item,
    );

const NAV_ITEMS: NavItem[] = applyRolloutHides([...BASE_NAV_ITEMS]);

export { NAV_ITEMS, NavItem, NavItemSubMenu, SubMenuItem };
